"use client";

import { useState, type KeyboardEvent } from "react";
import { ChevronDown } from "lucide-react";

interface CollapsibleSectionProps {
  title: string;
  subtitle?: string;
  subtitleColor?: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  className?: string;
  rightContent?: React.ReactNode;
  actionContent?: React.ReactNode;
}

export function CollapsibleSection({
  title,
  subtitle,
  subtitleColor,
  children,
  defaultExpanded = true,
  className = "",
  rightContent,
  actionContent,
}: CollapsibleSectionProps) {
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);
  const toggleSection = () => setIsExpanded((prev) => !prev);
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleSection();
    }
  };

  return (
    <div className={className}>
      <div
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        onClick={toggleSection}
        onKeyDown={handleKeyDown}
        className="w-full flex items-center justify-between gap-2 whitespace-nowrap text-sm font-medium p-4 rounded-lg bg-muted/20 hover:bg-muted/40 border border-border/30 hover:border-border/60 transition-all duration-200 group h-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0"
      >
        <div className="flex flex-col sm:flex-row sm:items-baseline items-start gap-1 sm:gap-4 flex-1">
          <div className="flex items-center justify-between w-full sm:w-auto">
            <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-black tracking-tight text-foreground group-hover:text-foreground/80">
              {title}
            </h2>
            <div className="sm:hidden">{rightContent}</div>
          </div>
          {subtitle && (
            <div className="flex items-center justify-between w-full sm:w-auto gap-6">
              <div className="flex items-baseline gap-0">
                <span
                  className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-black"
                  style={{ color: subtitleColor }}
                >
                  {subtitle}
                </span>
                <span className="text-sm sm:text-base md:text-lg font-normal text-foreground">
                  /100
                </span>
              </div>
              <div className="flex items-center gap-2 sm:hidden">
                {actionContent}
                <ChevronDown
                  className={`h-6 w-6 shrink-0 text-muted-foreground group-hover:text-foreground transition-transform duration-500 ease-in-out ${
                    isExpanded ? "rotate-0" : "rotate-180"
                  }`}
                  aria-hidden="true"
                />
              </div>
            </div>
          )}
        </div>

        <div className="hidden sm:flex items-center gap-2 sm:gap-3">
          {actionContent}
          {rightContent}
          <ChevronDown
            className={`h-8 w-8 sm:h-12 sm:w-12 md:h-14 md:w-14 lg:h-16 lg:w-16 shrink-0 text-muted-foreground group-hover:text-foreground transition-transform duration-500 ease-in-out ${
              isExpanded ? "rotate-0" : "rotate-180"
            }`}
            aria-hidden="true"
          />
        </div>
      </div>

      <div
        className={`overflow-hidden transition-all duration-500 ease-in-out ${
          isExpanded ? "max-h-[9999px]" : "max-h-0"
        }`}
        style={{
          transitionProperty: "max-height",
        }}
      >
        <div className="mt-6">{children}</div>
      </div>
    </div>
  );
}
